import { supabase } from '../lib/supabase';
import { whatCmsService } from '../lib/whatCmsService';
import { connectivityService } from '../lib/connectivityService';
import { callEdge } from '../lib/apiClient';
import { digitalScoreCalculator } from './DigitalScoreCalculator';
import { generateAICitations } from './aiCitationsService';

/**
 * Service pour lancer automatiquement toutes les analyses pour un nouveau prospect
 * Ce service est utilisé après la création d'un prospect pour générer automatiquement
 * toutes les données d'analyse nécessaires sans intervention manuelle de l'utilisateur
 */
export const AutoAnalysisService = {
  /**
   * Lance toutes les analyses pour un prospect donné
   * @param prospectId ID du prospect
   * @param siteUrl URL du site à analyser
   * @param companyName Nom de l'entreprise (optionnel)
   * @param location Localisation de l'entreprise (optionnel)
   */
  runAllAnalyses: async (
    prospectId: string,
    siteUrl: string,
    companyName?: string,
    location?: string
  ): Promise<boolean> => {
    console.log('🚀 [AutoAnalysisService] Démarrage des analyses pour:', {
      prospectId,
      siteUrl,
      companyName,
      location
    });

    try {
      if (!prospectId || !siteUrl) {
        console.error('❌ [AutoAnalysisService] Paramètres manquants');
        return false;
      }

      // Normaliser l'URL
      const normalizedUrl = normalizeUrl(siteUrl);
      const domain = extractDomain(siteUrl);
      console.log('📋 [AutoAnalysisService] URL normalisée:', { normalizedUrl, domain });

      // Si companyName ou location ne sont pas fournis, essayer de les récupérer depuis la base
      let finalCompanyName = companyName;
      let finalLocation = location;

      if (!finalCompanyName || !finalLocation) {
        console.log('📥 [AutoAnalysisService] Récupération des infos prospect depuis la DB');
        const { data: prospectData } = await supabase
          .from('prospects')
          .select('metadata, nom')
          .eq('id', prospectId)
          .single();

        if (prospectData) {
          finalCompanyName = finalCompanyName || prospectData.metadata?.company || prospectData.nom || domain;
          finalLocation = finalLocation || prospectData.metadata?.location || '';
          console.log('✅ [AutoAnalysisService] Infos récupérées:', { finalCompanyName, finalLocation });
        }
      }

      // Mettre à jour les métadonnées du prospect pour indiquer que l'analyse est en cours
      console.log('💾 [AutoAnalysisService] Mise à jour du statut: in_progress');
      await updateProspectMetadata(prospectId, {
        analyze_status: 'in_progress',
        analysis_started_at: new Date().toISOString(),
        company: finalCompanyName || domain,
        location: finalLocation || ''
      });

      // Exécuter toutes les analyses en parallèle (SAUF l'introduction personnalisée)
      console.log('⚡ [AutoAnalysisService] Lancement de 5 analyses en parallèle...');
      const startTime = Date.now();

      const results = await Promise.allSettled([
        // 1. Analyse PageSpeed
        analyzePageSpeed(normalizedUrl, prospectId),

        // 2. Détection CMS
        analyzeCms(normalizedUrl, prospectId),

        // 3. Analyse des mots-clés
        analyzeKeywords(domain, prospectId),

        // 4. Vérification de la connectivité
        checkConnectivity(normalizedUrl),

        // 5. Avis Google
        analyzeGoogleReviews(prospectId, domain, finalCompanyName, finalLocation)
      ]);

      const duration = ((Date.now() - startTime) / 1000).toFixed(2);
      const successCount = results.filter(result => result.status === 'fulfilled').length;
      const totalAnalyses = results.length;

      console.log(`✅ [AutoAnalysisService] Analyses terminées en ${duration}s:`, {
        succès: successCount,
        total: totalAnalyses,
        détails: results.map((r, i) => ({
          analyse: ['PageSpeed', 'CMS', 'Mots-clés', 'Connectivité', 'Google Reviews'][i],
          statut: r.status,
          erreur: r.status === 'rejected' ? r.reason : undefined
        }))
      });

      await updateProspectMetadata(prospectId, {
        analyze_status: 'completed',
        analysis_completed_at: new Date().toISOString(),
        analysis_success_count: successCount,
        analysis_total_count: totalAnalyses
      });

      // Générer les citations IA (en parallèle avec l'attente)
      console.log('🤖 [AutoAnalysisService] Génération des citations IA...');
      try {
        await generateAICitations(prospectId);
        console.log('✅ [AutoAnalysisService] Citations IA générées');
      } catch (citationsError) {
        console.warn('⚠️ [AutoAnalysisService] Erreur citations IA:', citationsError);
        // Continue même si les citations échouent
      }

      console.log('🤖 [AutoAnalysisService] Génération de l\'introduction personnalisée...');
      try {
        // Attendre un court délai pour s'assurer que les données sont bien commitées en base
        console.log('⏳ [AutoAnalysisService] Attente de 2s pour la propagation des données...');
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Fetch all data via Edge Function (service role key, no 406 errors)
        const allData = await callEdge<{ prospectId: string }, any>('get-prospect-data', {
          prospectId
        });

        console.log('📊 [AutoAnalysisService] Données récupérées:', {
          hasProspect: !!allData.prospect,
          hasPagespeed: !!allData.pagespeedData,
          hasCms: !!allData.cmsData,
          hasKeywords: Array.isArray(allData.keywordsData) && allData.keywordsData.length > 0,
          hasGoogleReviews: !!allData.googleReviews
        });

        await generatePersonalizedIntroWithRealData(prospectId, domain, finalCompanyName, allData);
        console.log('✅ [AutoAnalysisService] Introduction personnalisée générée');

        // Générer les 3 recommandations IA
        console.log('🎯 [AutoAnalysisService] Génération des 3 recommandations IA...');
        try {
          await generateAIRecommendations(prospectId, domain, finalCompanyName, allData);
          console.log('✅ [AutoAnalysisService] 3 recommandations IA générées');
        } catch (recoError) {
          console.warn('⚠️ [AutoAnalysisService] Erreur lors de la génération des recommandations:', recoError);
          // Continue même si les recommandations échouent
        }
      } catch (introError) {
        console.warn('⚠️ [AutoAnalysisService] Erreur intro, utilisation du placeholder:', introError);
        await generatePersonalizedIntroPlaceholder(prospectId, domain, finalCompanyName);
      }

      console.log('🎉 [AutoAnalysisService] Toutes les analyses terminées avec succès');
      return true;
    } catch (error) {
      console.error('❌ [AutoAnalysisService] Erreur globale:', error);
      try {
        await updateProspectMetadata(prospectId, {
          analyze_status: 'error',
          analysis_error: error instanceof Error ? error.message : String(error)
        });
      } catch (updateError) {
        console.error('❌ [AutoAnalysisService] Erreur mise à jour métadonnées:', updateError);
      }

      return false;
    }
  }
};

/**
 * Normalise une URL en ajoutant le protocole si nécessaire
 */
function normalizeUrl(url: string): string {
  if (!url) return '';
  
  let formattedUrl = url.trim();
  if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
    formattedUrl = 'https://' + formattedUrl;
  }
  
  return formattedUrl;
}

/**
 * Extrait le domaine à partir d'une URL
 */
function extractDomain(url: string): string {
  if (!url) return '';
  
  try {
    const urlObj = new URL(normalizeUrl(url));
    const hostname = urlObj.hostname;
    return hostname.startsWith('www.') ? hostname.substring(4) : hostname;
  } catch (e) {
    return url;
  }
}

/**
 * Met à jour les métadonnées d'un prospect
 */
async function updateProspectMetadata(prospectId: string, metadata: Record<string, any>): Promise<void> {
  try {
    // Récupérer d'abord les métadonnées existantes
    const { data: prospectData } = await supabase
      .from('prospects')
      .select('metadata')
      .eq('id', prospectId)
      .single() as any;
  
    // Fusionner les métadonnées existantes avec les nouvelles
    const updatedMetadata = {
      ...((prospectData?.metadata as Record<string, any>) || {}),
      ...metadata
    };
  
    // Mettre à jour les métadonnées
    await (supabase as any)
      .from('prospects')
      .update({
        metadata: updatedMetadata,
        updated_at: new Date().toISOString()
      })
      .eq('id', prospectId);
  } catch (error) {
    throw error;
  }
}

/**
 * Analyse la performance PageSpeed d'un site
 */
async function analyzePageSpeed(url: string, prospectId: string): Promise<boolean> {
  console.log('🏃 [PageSpeed] Appel Edge Function:', { url, prospectId });
  try {
    const results = await callEdge('pagespeed', { url, prospectId });
    console.log('✅ [PageSpeed] Résultat reçu:', results ? 'Données disponibles' : 'Aucune donnée');
    return !!results;
  } catch (error) {
    console.error('❌ [PageSpeed] Erreur:', error);
    return false;
  }
}

/**
 * Détecte le CMS d'un site
 */
async function analyzeCms(url: string, prospectId: string): Promise<boolean> {
  console.log('🔍 [CMS] Appel WhatCMS API:', { url });
  try {
    const result = await whatCmsService.detectCms(url);
    console.log('✅ [CMS] Résultat:', result ? `${result.cms} détecté` : 'Aucun CMS détecté');

    if (prospectId && result) {
      const cmsData: any = {
        prospect_id: prospectId,
        url: url,
        detected: result.detected,
        cms_name: result.cms,
        confidence: result.confidence,
        version: result.version || null,
        logo_url: result.logo || null,
        details: result.details || null,
        technologies: result.technologies || null,
        social_profiles: result.socialProfiles || null,
      };

      await supabase
        .from('cms_detection_results')
        .upsert(cmsData, { onConflict: 'prospect_id' });
      console.log('💾 [CMS] Données sauvegardées dans la DB');

      return true;
    }

    return false;
  } catch (error) {
    console.error('❌ [CMS] Erreur:', error);
    return false;
  }
}

/**
 * Analyse les mots-clés d'un domaine
 */
async function analyzeKeywords(domain: string, prospectId: string): Promise<boolean> {
  console.log('🔑 [Keywords] Appel Edge Function:', { domain, prospectId });
  try {
    // Use cache-first approach (no forceRefresh)
    const response = await callEdge<{ domain: string; prospectId: string }, { keywords: any[]; cached: boolean; timestamp: string }>('dataforseo-keywords', { domain, prospectId });
    console.log('🔍 [Keywords] Réponse complète:', response);
    const keywords = response?.keywords || [];
    const hasData = Array.isArray(keywords) && keywords.length > 0;
    console.log('✅ [Keywords] Résultat:', hasData ? `${keywords.length} mots-clés trouvés` : 'Aucun mot-clé', { cached: response?.cached, keywordsType: typeof keywords, isArray: Array.isArray(keywords) });
    return hasData;
  } catch (error) {
    console.error('❌ [Keywords] Erreur:', error);
    return false;
  }
}

/**
 * Vérifie la connectivité d'un site
 */
async function checkConnectivity(url: string): Promise<boolean> {
  console.log('🌐 [Connectivity] Vérification de:', url);
  try {
    const result = await connectivityService.checkConnectivityWithFallback(url);
    console.log('✅ [Connectivity] Résultat:', result.isAccessible ? 'Accessible' : 'Non accessible');
    return result.isAccessible;
  } catch (error) {
    console.error('❌ [Connectivity] Erreur:', error);
    return false;
  }
}

/**
 * Analyse les avis Google d'une entreprise
 */
async function analyzeGoogleReviews(
  prospectId: string,
  domain: string,
  companyName?: string,
  location?: string
): Promise<boolean> {
  console.log('⭐ [Google Reviews] Appel Edge Function:', {
    prospectId,
    domain,
    businessName: companyName || domain,
    location: location || undefined
  });
  try {
    if (!companyName && !domain) {
      console.warn('⚠️ [Google Reviews] Aucun nom d\'entreprise fourni');
      return false;
    }

    const params = {
      prospectId,
      domain,
      businessName: companyName || domain,
      ...(location && { location }) // Only include location if provided
    };

    console.log('📤 [Google Reviews] Paramètres envoyés:', params);

    const result = await callEdge('google-reviews-fetch', params);
    console.log('✅ [Google Reviews] Réponse reçue:', result);

    return true;
  } catch (error) {
    console.error('❌ [Google Reviews] Erreur:', error);
    return false;
  }
}

/**
 * Génère un placeholder d'introduction personnalisée pour un prospect
 * Utilisé en cas d'erreur lors de la génération avec IA
 */
async function generatePersonalizedIntroPlaceholder(
  prospectId: string,
  domain: string,
  companyName?: string
): Promise<boolean> {
  try {
    const introData: any = {
      prospect_id: prospectId,
      content: `Analyse en cours pour ${companyName || domain}. Votre introduction personnalisée sera générée dans quelques instants...`
    };
    
    // Utiliser upsert avec la contrainte prospect_id
    const { error } = await supabase
      .from('personalized_intros')
      .upsert(introData, { 
        onConflict: 'prospect_id',
        ignoreDuplicates: false // Mettre à jour en cas de conflit
      });
      
    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Génère une introduction personnalisée AVEC LES VRAIES DONNÉES
 * Cette fonction est appelée APRÈS que toutes les analyses soient terminées
 */
async function generatePersonalizedIntroWithRealData(
  prospectId: string,
  domain: string,
  companyName: string | undefined,
  allData: any
): Promise<boolean> {
  try {
    // Extract data from Edge Function response
    const prospect = allData.prospect;
    const pagespeed = allData.pagespeedData;
    const cms = allData.cmsData;
    const keywords = allData.keywordsData || [];
    const googleReviews = allData.googleReviews;

    // Vérifier si on a assez de données réelles pour générer une intro pertinente
    const hasRealData = (
      (pagespeed && pagespeed.mobile_performance_score !== undefined) ||
      (cms && cms.cms_name) ||
      (keywords.length > 0) ||
      (googleReviews && googleReviews.rating)
    );

    if (!hasRealData) {
      console.warn('⚠️ [AutoAnalysisService] Pas assez de données réelles disponibles, utilisation du placeholder');
      await generatePersonalizedIntroPlaceholder(prospectId, domain, companyName);
      return false;
    }

    // Construire les informations pour le prompt
    const businessName = companyName || prospect?.metadata?.company || domain;
    const industry = prospect?.metadata?.industry || 'Non spécifié';
    const description = prospect?.metadata?.description || '';
    
    // 🎯 NOUVEAU : Calculer le score digital COMPOSITE multi-dimensionnel
    // Utilise TOUTES les données disponibles : PageSpeed, Keywords, Google Reviews, CMS, Connectivity
    const scoreBreakdown = digitalScoreCalculator.calculateScore({
      pagespeed,
      keywords,
      googleReviews,
      cms,
      connectivity: { isAccessible: true, hasSSL: true } // On assume accessible si on arrive ici
    });

    const scoreDigital = scoreBreakdown.global;
    
    // Extraire les scores détaillés pour le prompt
    const pageSpeedMobile = scoreBreakdown.performance.mobile;
    const pageSpeedDesktop = scoreBreakdown.performance.desktop;

    console.log('🎯 [AutoAnalysisService] Score composite calculé:', {
      global: scoreDigital,
      performance: scoreBreakdown.performance.score,
      seo: scoreBreakdown.seo.score,
      reputation: scoreBreakdown.reputation.score,
      maturity: scoreBreakdown.maturity.score,
      infrastructure: scoreBreakdown.infrastructure.score
    });
    
    // Informations CMS
    const cmsName = cms?.cms_name || null;
    const cmsVersion = cms?.version || null;
    const cmsDescription = cms?.details || '';
    const hasCms = !!cmsName && cmsName !== 'Non détecté';
    
    // Mots-clés et avis
    const keywordsCount = keywords.length;
    const keywordsList = keywords.map((k: any) => k.keyword).slice(0, 5).join(', ');
    const googleReviewSummary = googleReviews ? `${googleReviews.rating}/5 (${googleReviews.reviews_count} avis)` : null;

    console.log('📝 [AutoAnalysisService] Données RÉELLES extraites pour le prompt:', {
      businessName,
      scoreDigital,
      scoreBreakdown: {
        performance: scoreBreakdown.performance.score,
        seo: scoreBreakdown.seo.score,
        reputation: scoreBreakdown.reputation.score,
        maturity: scoreBreakdown.maturity.score,
        infrastructure: scoreBreakdown.infrastructure.score
      },
      pageSpeedMobile,
      pageSpeedDesktop,
      cmsName,
      cmsVersion,
      keywordsCount,
      hasGoogleReviews: !!googleReviews,
      googleRating: googleReviews?.rating
    });

    //  ENREGISTRER LE SCORE CALCULÉ DANS LA BASE DE DONNÉES
    console.log(` [AutoAnalysisService] Enregistrement du score ${scoreDigital}/100 en base de données...`);
    try {
      const { error: scoreError } = await supabase
        .from('prospects')
        .update({ score: scoreDigital } as any)
        .eq('id', prospectId);
      
      if (scoreError) {
        console.error(' [AutoAnalysisService] Erreur lors de l\'enregistrement du score:', scoreError);
      } else {
        console.log(` [AutoAnalysisService] Score ${scoreDigital}/100 enregistré avec succès`);
      }
    } catch (scoreErr) {
      console.error(' [AutoAnalysisService] Exception lors de l\'enregistrement du score:', scoreErr);
    }

    // Construire le prompt COMPLET pour l'IA avec les données RÉELLES EXACTES (score ${scoreDigital}/100)
    const prompt = `
Tu es un expert senior en webmarketing et transformation digitale. Tu rédiges une introduction d'audit digital personnalisée pour "${businessName}".

═══════════════════════════════════════════════════════════════════
 DONNÉES D'AUDIT MESURÉES (UTILISE CES CHIFFRES EXACTS)
═══════════════════════════════════════════════════════════════════

ENTREPRISE :
- Nom : ${businessName}
- Secteur : ${industry}
${description ? `- Description : ${description}` : ''}
- Site web : ${domain}

PERFORMANCES TECHNIQUES (Score composite multi-dimensionnel) :
-  Score digital global : ${scoreDigital}/100 (composite 5 piliers)
  • Performance Technique : ${scoreBreakdown.performance.score}/100
  • Optimisation SEO : ${scoreBreakdown.seo.score}/100
  • Réputation : ${scoreBreakdown.reputation.score}/100
  • Maturité Digitale : ${scoreBreakdown.maturity.score}/100
  • Infrastructure : ${scoreBreakdown.infrastructure.score}/100
- Performance Mobile : ${pageSpeedMobile !== null ? pageSpeedMobile + '%' : 'en cours d\'analyse'}
- Performance Desktop : ${pageSpeedDesktop !== null ? pageSpeedDesktop + '%' : 'en cours d\'analyse'}
${hasCms ? `- Technologie web : ${cmsName}${cmsVersion ? ' (version ' + cmsVersion + ')' : ''} ${cmsDescription ? '- ' + cmsDescription : ''}` : '- Technologie web : Non détectée'}

VISIBILITÉ SEO :
- Nombre de mots-clés positionnés : ${keywordsCount}
${keywordsList ? `- Principaux mots-clés : ${keywordsList}` : '- Mots-clés : En cours d\'analyse'}
- Potentiel de trafic organique : ${keywordsCount > 15 ? 'Élevé' : keywordsCount > 8 ? 'Moyen' : 'Faible'}

RÉPUTATION EN LIGNE :
${googleReviewSummary ? `- Avis clients Google : ${googleReviewSummary}` : '- Avis clients : Non disponibles'}

═══════════════════════════════════════════════════════════════════
 INSTRUCTIONS CRITIQUES (À SUIVRE ABSOLUMENT)
═══════════════════════════════════════════════════════════════════

1. **CHIFFRES EXACTS** : Tu DOIS utiliser EXACTEMENT ces valeurs :
   → Mobile : ${pageSpeedMobile}%
   → Desktop : ${pageSpeedDesktop}%
   Ne JAMAIS inventer, arrondir ou modifier ces chiffres !

2. **ÉLÉMENTS OBLIGATOIRES** à mentionner dans le texte :
   ${hasCms ? ` La technologie ${cmsName} détectée sur le site` : ' Aucun CMS détecté (mention optionnelle)'}
   Les ${keywordsCount} mots-clés positionnés dans Google
   ${googleReviewSummary ? ` La note ${googleReviewSummary} des avis clients` : ''}

3. **ANALYSE CONTEXTUELLE** :
   ${pageSpeedMobile !== null && pageSpeedMobile < 50 ? '→ Performance mobile CRITIQUE (< 50%) - À souligner comme point d\'amélioration prioritaire' : ''}
   ${pageSpeedMobile !== null && pageSpeedMobile >= 50 && pageSpeedMobile < 70 ? '→ Performance mobile MOYENNE (50-70%) - Marge d\'amélioration significative' : ''}
   ${pageSpeedMobile !== null && pageSpeedMobile >= 70 ? '→ Performance mobile BONNE (≥ 70%) - Point fort à valoriser' : ''}
   ${keywordsCount < 10 ? '→ Visibilité SEO FAIBLE (< 10 mots-clés) - Opportunité majeure d\'optimisation' : ''}
   ${keywordsCount >= 10 && keywordsCount < 20 ? '→ Visibilité SEO MOYENNE (10-20 mots-clés) - Potentiel de croissance' : ''}
   ${keywordsCount >= 20 ? '→ Visibilité SEO SOLIDE (≥ 20 mots-clés) - Base à optimiser' : ''}

═══════════════════════════════════════════════════════════════════
 FORMAT DE SORTIE (STRUCTURE IMPOSÉE)
═══════════════════════════════════════════════════════════════════

Rédige une introduction de 100-140 mots, structurée en 2 paragraphes séparés par \\n\\n.

**PARAGRAPHE 1** (50-70 mots) - Analyse technique factuelle :
→ Détaille les performances mesurées : ${pageSpeedMobile}% mobile et ${pageSpeedDesktop}% desktop
${hasCms ? `→ Mentionne l'utilisation de ${cmsName} comme plateforme web` : ''}
→ Cite les ${keywordsCount} mots-clés positionnés dans Google Suisse
${googleReviewSummary ? `→ Intègre la réputation ${googleReviewSummary}` : ''}
→ Ton : Factuel, précis, professionnel

**PARAGRAPHE 2** (50-70 mots) - Projection et opportunités :
→ Identifie les 2-3 axes d'amélioration prioritaires basés sur les données réelles
→ Quantifie le potentiel (ex: "augmenter le trafic de X%", "réduire le temps de chargement")
→ Mentionne les opportunités concrètes (SEO, performance mobile, automatisation, IA)
→ Ton : Actionnable, orienté business, sans hype

═══════════════════════════════════════════════════════════════════
 RÈGLES D'ÉCRITURE STRICTES
═══════════════════════════════════════════════════════════════════

À ÉVITER ABSOLUMENT :
 "je", "nous", "notre", "votre", "vous"
 "découvrez", "profitez", "ne manquez pas"
 Formules marketing creuses et génériques
 Superlatifs exagérés ("extraordinaire", "révolutionnaire")
 Promesses non justifiées par les données

À PRIVILÉGIER :
 3e personne uniquement ("L'analyse révèle...", "Le site présente...")
 Verbes d'action concrets ("optimiser", "améliorer", "accélérer")
 Données chiffrées exactes
 Ton professionnel, humain mais factuel
 Lien direct entre les données et les recommandations

═══════════════════════════════════════════════════════════════════

Génère maintenant l'introduction en respectant TOUTES ces instructions.
`;

    console.log(' [AutoAnalysisService] Prompt envoyé à OpenAI:', prompt.substring(0, 300) + '...');

    // Générer l'introduction avec l'IA via Edge Function
    const response = await callEdge<{ prompt: string }, { recommendations: string }>('openai-recommendations', { prompt });
    const generatedIntro = response?.recommendations || '';

    if (generatedIntro && generatedIntro.trim().length > 10) {
      // Sauvegarder l'introduction dans la base de données
      const introData: any = {
        prospect_id: prospectId,
        content: generatedIntro.trim()
      };
      
      const { error } = await supabase
        .from('personalized_intros')
        .upsert(introData, { 
          onConflict: 'prospect_id',
          ignoreDuplicates: false
        });
        
      if (error) {
        throw error;
      }

      return true;
    } else {
      return false;
    }
  } catch (error) {
    throw error;
  }
}
/**
 * Générer 3 recommandations courtes et actionnables avec l'IA
 * @param prospectId ID du prospect
 * @param domain Domaine du site
 * @param companyName Nom de l'entreprise
 * @param allData Toutes les données d'analyse
 * @returns true si généré avec succès
 */
async function generateAIRecommendations(
  prospectId: string,
  domain: string,
  companyName: string | undefined,
  allData: any
): Promise<boolean> {
  try {
    // Extract data from Edge Function response
    const prospect = allData.prospect;
    const pagespeed = allData.pagespeedData;
    const cms = allData.cmsData;
    const keywords = allData.keywordsData || [];
    const googleReviews = allData.googleReviews;

    // Vérifier si on a assez de données réelles
    const hasRealData = (
      (pagespeed && pagespeed.mobile_performance_score !== undefined) ||
      (cms && cms.cms_name) ||
      (keywords.length > 0) ||
      (googleReviews && googleReviews.rating)
    );

    if (!hasRealData) {
      console.warn('⚠️ [AutoAnalysisService] Pas assez de données pour générer des recommandations IA');
      return false;
    }

    // Construire les informations pour le prompt
    const businessName = companyName || prospect?.metadata?.company || domain;

    // Calculer le score digital
    const scoreBreakdown = digitalScoreCalculator.calculateScore({
      pagespeed,
      keywords,
      googleReviews,
      cms,
      connectivity: { isAccessible: true, hasSSL: true }
    });

    const scoreDigital = scoreBreakdown.global;
    const pageSpeedMobile = scoreBreakdown.performance.mobile;
    const pageSpeedDesktop = scoreBreakdown.performance.desktop;
    const cmsName = cms?.cms_name || null;
    const keywordsCount = keywords.length;
    const googleRating = googleReviews?.rating;
    const googleReviewsCount = googleReviews?.reviews_count;

    console.log('🎯 [AutoAnalysisService] Génération de 3 recommandations IA pour:', {
      businessName,
      scoreDigital,
      pageSpeedMobile,
      keywordsCount
    });

    // Prompt spécifique pour les 3 recommandations
    const prompt = `
Tu es un expert senior en webmarketing. Génère EXACTEMENT 3 recommandations courtes et actionnables pour "${businessName}".

═══════════════════════════════════════════════════════════════════
 DONNÉES D'AUDIT MESURÉES
═══════════════════════════════════════════════════════════════════

PERFORMANCES :
- Mobile : ${pageSpeedMobile}%
- Desktop : ${pageSpeedDesktop}%
${cmsName ? `- CMS : ${cmsName}` : ''}

SEO :
- Mots-clés positionnés : ${keywordsCount}

RÉPUTATION :
${googleRating ? `- Avis Google : ${googleRating}/5 (${googleReviewsCount} avis)` : '- Aucun avis Google trouvé'}

═══════════════════════════════════════════════════════════════════
 INSTRUCTIONS
═══════════════════════════════════════════════════════════════════

Génère EXACTEMENT 3 recommandations qui :
1. Sont basées sur les DONNÉES RÉELLES ci-dessus
2. Incluent des CHIFFRES PRÉCIS (ex: "passer de ${pageSpeedMobile}% à 75%")
3. Sont ACTIONNABLES immédiatement
4. Ont un IMPACT BUSINESS clair
5. Sont PRIORISÉES (quick wins d'abord)

FORMAT DE SORTIE (STRICT) :
Retourne un JSON array avec exactement 3 strings. Chaque recommandation :
- Maximum 15 mots
- Commence par un verbe d'action
- Inclut un chiffre ou métrique
- Pas de numérotation (1., 2., 3.)
- Pas de bullet points (•, -, *)

EXEMPLE DE FORMAT ATTENDU :
["Améliorer la vitesse mobile de ${pageSpeedMobile}% à 75% pour réduire le taux de rebond", "Cibler 10 nouveaux mots-clés SEO à fort potentiel (actuellement ${keywordsCount} positionnés)", "Obtenir 15 avis Google supplémentaires pour améliorer la visibilité locale"]

RÈGLES STRICTES :
- TOUJOURS inclure les chiffres exacts des données
- JAMAIS utiliser "vous", "votre", "nous"
- TOUJOURS être concret et mesurable
- JAMAIS de promesses vagues

Génère maintenant le JSON array avec les 3 recommandations :`;

    console.log('💡 [AutoAnalysisService] Prompt recommandations envoyé à OpenAI');

    // Générer les recommandations avec l'IA
    const response = await callEdge<{ prompt: string }, { recommendations: string }>('openai-recommendations', { prompt });
    const generatedText = response?.recommendations || '';

    if (!generatedText || generatedText.trim().length < 10) {
      console.warn('⚠️ [AutoAnalysisService] Réponse IA vide ou trop courte');
      return false;
    }

    // Parser le JSON (l'IA devrait retourner un array JSON)
    let recommendations: string[];
    try {
      // Tenter de parser directement
      recommendations = JSON.parse(generatedText.trim());
    } catch (parseError) {
      // Si échec, essayer d'extraire le JSON d'une réponse markdown
      const jsonMatch = generatedText.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        recommendations = JSON.parse(jsonMatch[0]);
      } else {
        // Fallback : splitter par lignes
        recommendations = generatedText
          .split('\n')
          .filter(line => line.trim().length > 10)
          .map(line => line.replace(/^[-•*\d.)\s]+/, '').trim())
          .slice(0, 3);
      }
    }

    // Valider qu'on a bien 3 recommandations
    if (!Array.isArray(recommendations) || recommendations.length === 0) {
      console.warn('⚠️ [AutoAnalysisService] Format de recommandations invalide');
      return false;
    }

    // Limiter à 3 recommandations maximum
    recommendations = recommendations.slice(0, 3);

    console.log('✅ [AutoAnalysisService] 3 recommandations générées:', recommendations);

    // Sauvegarder dans la table ai_recommendations
    // Colonne analysis: 3 recommandations en JSON string (TEXT)
    // Colonne recommendations: 3 recommandations en JSON array (JSONB)
    console.log('💾 [AutoAnalysisService] Tentative de sauvegarde pour prospect_id:', prospectId);
    console.log('💾 [AutoAnalysisService] Données à sauvegarder:', {
      prospect_id: prospectId,
      analysis: JSON.stringify(recommendations),
      recommendations: JSON.stringify(recommendations)
    });

    const { data: insertedData, error } = await supabase
      .from('ai_recommendations')
      .upsert({
        prospect_id: prospectId,
        analysis: JSON.stringify(recommendations),
        recommendations: JSON.stringify(recommendations)
      }, {
        onConflict: 'prospect_id',
        ignoreDuplicates: false
      })
      .select();

    if (error) {
      console.error('❌ [AutoAnalysisService] Erreur lors de la sauvegarde des recommandations:', {
        error,
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      throw error;
    }

    console.log('✅ [AutoAnalysisService] Recommandations sauvegardées en base de données:', insertedData);
    return true;

  } catch (error) {
    console.error('❌ [AutoAnalysisService] Erreur lors de la génération des recommandations:', error);
    // Re-throw database errors to ensure visibility
    if (error && typeof error === 'object' && 'code' in error) {
      console.error('❌ [AutoAnalysisService] Database error details:', {
        code: (error as any).code,
        message: (error as any).message,
        details: (error as any).details
      });
    }
    throw error; // Re-throw to propagate to caller
  }
}
